import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import MDBox from "components/MDBox";
import { createCapa } from "app/services/iqms.service";

const CAPA_TYPES = ["CORRECTIVE", "PREVENTIVE"];
const CAPA_PRIORITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
const CAPA_STATUSES = ["OPEN", "IN_PROGRESS", "CLOSED", "OVERDUE", "PENDING_VERIFICATION"];

const initialData = {
  id: null,
  capaCode: "",
  title: "",
  description: "",
  type: "CORRECTIVE",
  priority: "MEDIUM",
  status: "OPEN",
  assignee: "",
  rootCause: "",
  dueDate: "",
};

const AddCapaDialog = ({ open, setOpen, onSuccess, editObject }) => {
  const [data, setData] = useState(initialData);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editObject) {
      setData({
        ...editObject,
        dueDate: editObject.dueDate?.substring(0, 10) || "",
      });
    } else {
      setData(initialData);
    }
    setMessageOpen(false);
  }, [editObject, open]);

  const validateForm = () => {
    if (!data.capaCode || data.capaCode.trim() === "") {
      setError("CAPA code is required");
      return false;
    }
    if (!data.title || data.title.trim() === "") {
      setError("Title is required");
      return false;
    }
    return true;
  };

  const setError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await createCapa(data);
      setSuccess("CAPA created successfully");
      setTimeout(() => {
        setData(initialData);
        setMessageOpen(false);
        setOpen(false);
        onSuccess && onSuccess();
      }, 1000);
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setData(initialData);
    setMessageOpen(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editObject ? "Edit CAPA" : "Create New CAPA"}</DialogTitle>
      <DialogContent>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="capaCode"
            label="CAPA Code"
            fullWidth
            variant="outlined"
            value={data.capaCode}
            onChange={(e) => setData({ ...data, capaCode: e.target.value })}
            required
          />
          <TextField
            id="type"
            label="Type"
            fullWidth
            select
            variant="outlined"
            value={data.type}
            onChange={(e) => setData({ ...data, type: e.target.value })}
          >
            {CAPA_TYPES.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="title"
            label="Title"
            fullWidth
            variant="outlined"
            value={data.title}
            onChange={(e) => setData({ ...data, title: e.target.value })}
            required
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="priority"
            label="Priority"
            fullWidth
            select
            variant="outlined"
            value={data.priority}
            onChange={(e) => setData({ ...data, priority: e.target.value })}
          >
            {CAPA_PRIORITIES.map((p) => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </TextField>
          <TextField
            id="status"
            label="Status"
            fullWidth
            select
            variant="outlined"
            value={data.status}
            onChange={(e) => setData({ ...data, status: e.target.value })}
          >
            {CAPA_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s.replace("_", " ")}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="assignee"
            label="Assignee"
            fullWidth
            variant="outlined"
            value={data.assignee}
            onChange={(e) => setData({ ...data, assignee: e.target.value })}
          />
          <TextField
            id="dueDate"
            label="Due Date"
            type="date"
            fullWidth
            variant="outlined"
            value={data.dueDate}
            onChange={(e) => setData({ ...data, dueDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="rootCause"
            label="Root Cause"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={data.rootCause}
            onChange={(e) => setData({ ...data, rootCause: e.target.value })}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            severity={severity}
            action={
              <IconButton size="small" onClick={() => setMessageOpen(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {editObject ? "Update" : "Create"}
        </Button>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddCapaDialog);

